import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import cn from 'classnames';
import { FC, ReactNode, useState } from 'react';
import { useIntl } from 'react-intl';

import { Checkbox, Icon, PlainLexicalEditor } from '@/shared/ui';

import { StoredSection } from '@/entities/templates';

import { templateEditorMessages } from '../../config';
import { EditorButton } from '../EditorButton';

import { DragHandleGlyph } from './DragHandleGlyph';
import styles from './SectionBlock.module.scss';

type SectionBlockProps = {
  id: string;
  sectionIndex: number;
  section: StoredSection;
  children: ReactNode;
  className?: string;
  isActive?: boolean;
  hasError?: boolean;
  readOnly?: boolean;
  autoFocus?: boolean;
  onTitleChange: (index: number, title: string) => void;
  onDeleteSection: (index: number) => void;
  onRequiredChange: (index: number, required: boolean) => void;
  onOpenAiInstructions: (index: number) => void;
  onFocus?: (index: number) => void;
  onEnterPress?: (index: number) => void;
  onTabPress?: (index: number, isShiftPressed: boolean) => void;
};

export const SectionBlock: FC<SectionBlockProps> = (props) => {
  const {
    id,
    sectionIndex,
    section,
    className,
    isActive,
    hasError,
    readOnly,
    autoFocus,
    children,
    onTitleChange,
    onDeleteSection,
    onRequiredChange,
    onOpenAiInstructions,
    onFocus,
    onEnterPress,
    onTabPress,
  } = props;

  const { formatMessage } = useIntl();

  const [isHovered, setIsHovered] = useState(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    disabled: readOnly,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleTitleChange = (value: string) => {
    onTitleChange(sectionIndex, value);
  };

  const handleDelete = () => {
    onDeleteSection(sectionIndex);
  };

  const showControls = Boolean(isActive) || isHovered;
  const hasInstructions = Boolean(section.instructions?.trim());
  const isOptional = section.required === false;

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-component="template-section-block"
      className={cn(
        styles.section,
        isActive && styles.active,
        hasError && styles.hasError,
        isDragging && styles.dragging,
        className,
      )}
      onClick={() => onFocus?.(sectionIndex)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.header}>
        <span
          className={cn(
            styles.handle,
            (isHovered || isDragging) && styles.handleVisible,
          )}
          title={formatMessage(templateEditorMessages.dragHandle)}
          {...attributes}
          {...(readOnly ? {} : listeners)}
        >
          <DragHandleGlyph />
        </span>
        <span
          className={cn('p4', styles.number)}
          {...(readOnly ? {} : listeners)}
        >
          {sectionIndex + 1}
        </span>

        <PlainLexicalEditor
          className={styles.titleEditor}
          value={section.title}
          placeholder={formatMessage(templateEditorMessages.sectionTitle)}
          readOnly={readOnly}
          autoFocus={autoFocus}
          onFocus={() => onFocus?.(sectionIndex)}
          onEnter={() => onEnterPress?.(sectionIndex)}
          onTab={(isShiftPressed) => onTabPress?.(sectionIndex, isShiftPressed)}
          onChange={handleTitleChange}
        />

        {showControls && !readOnly ? (
          <Checkbox
            className={styles.optionalCheckbox}
            checked={isOptional}
            onChange={() => onRequiredChange(sectionIndex, isOptional)}
          >
            <span className={styles.optionalLabel}>
              {formatMessage(templateEditorMessages.sectionOptional)}
            </span>
          </Checkbox>
        ) : (
          isOptional && (
            <span className={cn('p4', styles.optionalTag)}>
              {formatMessage(templateEditorMessages.sectionOptional)}
            </span>
          )
        )}

        {hasInstructions || (!readOnly && showControls) ? (
          <EditorButton
            variant="icon"
            disabled={readOnly}
            className={cn(
              styles.aiButton,
              hasInstructions && styles.aiButtonFilled,
            )}
            title={formatMessage(templateEditorMessages.aiInstructionsTitle)}
            onClick={() => onOpenAiInstructions(sectionIndex)}
          >
            <Icon
              name={hasInstructions ? 'comment2Filled' : 'comment2'}
              size={18}
            />
          </EditorButton>
        ) : (
          <span className={styles.iconPlaceholder} />
        )}

        {!readOnly && showControls ? (
          <EditorButton
            variant="icon"
            className={styles.deleteButton}
            title={formatMessage(templateEditorMessages.deleteSection)}
            onClick={handleDelete}
          >
            <Icon name="delete" size={18} />
          </EditorButton>
        ) : (
          <span className={styles.iconPlaceholder} />
        )}
      </div>

      <div className={styles.optionsArea}>{children}</div>
    </div>
  );
};
